import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StateSelection from './StateSelection';
import SurgeonSelection from './SurgeonSelection';
import SurgeryTypeSelection from './SurgeryTypeSelection';
import SatisfactionSelection from './SatisfactionSelection';
import ReviewForm from './ReviewForm';
import DemographicInfo from './DemographicInfo';
import { api } from '../services/api';
import { helpers } from '../services/helpers';
import SurgeryPrivacy from './SurgeryPrivacy';

function ReviewFormWrapper({ user, toast }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    state: null,
    surgeon: null,
    procedures: [],
    rating: 0,
    description: '',
    surgeryDate: '',
    id: 0,
    public: true
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState([]);
  const [customState, setCustomState] = useState('');
  const [steps, setSteps] = useState(["state", "surgeon", "procedure", "date", "satisfaction", "privacy", "review"])

  useEffect(() => {
    if (!user) {
      navigate('/surgeries');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesData = await api.getStates();
        setStates(statesData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load states');
        setLoading(false);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    if (id) {
      api.getReview(id)
        .then(res => {
          if (res.updates.length > 1) {
            setSteps(["state", "surgeon", "procedure", "date", "privacy"])
          }
          setFormData(helpers.reviewToFormData(res))
        })
    }
    const fetchSurgeryTypes = async () => {
      try {
        setLoading(true);
        const data = await api.getSurgeryTypes();
        setSurgeryTypes(data);
      } catch (err) {
        setError('Failed to load surgery types. Please try again later.');
        console.error('Error fetching surgery types:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSurgeryTypes();
  }, []);


  const onContinue = () => {
    if (currentStep === steps.length) {
      saveReview()
    } else {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const saveReview = () => {
    setSaving(true);
    api.saveReview(helpers.formDataToReview(formData))
      .then(() => {
        toast(
          `${formData.id ? 'Surgery Edited Successfully' : 'Surgery Submitted Successfully'}`
        )
        navigate('/surgeries')
      }).finally(() => setSaving(false))
  }

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleCancel = () => {
    navigate('/surgeries');
  };

  return (
    <div className="form-container">
      {steps[currentStep - 1] === "state" && (
        <StateSelection
          onContinue={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onCancel={handleCancel}
          editing={!!formData.id}
          states={states}
          formData={formData}
          setFormData={setFormData}
          setCustomState={setCustomState}
        />
      )}
      {steps[currentStep - 1] === "surgeon" && (
        <SurgeonSelection
          onContinue={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          states={states}
          formData={formData}
          setFormData={setFormData}
          customState={customState}
          setCustomState={setCustomState}
        />
      )}
      {steps[currentStep - 1] === "procedure" && (
        <SurgeryTypeSelection
          onContinue={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          surgeryTypes={surgeryTypes}
          setSurgeryTypes={setSurgeryTypes}
          loading={loading}
          error={error}
          formData={formData}
          setFormData={setFormData}
        />
      )}
      {steps[currentStep - 1] === "date" && (
        <DemographicInfo
          onSubmit={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          editing={!!formData.id}
          saving={saving}
          formData={formData}
          setFormData={setFormData}
        />
      )}
      {steps[currentStep - 1] === "satisfaction" && (
        <SatisfactionSelection
          onContinue={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          formData={formData}
          setFormData={setFormData}
        />
      )}
      {steps[currentStep - 1] === "privacy" && (
        <SurgeryPrivacy
          onContinue={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          formData={formData}
          setFormData={setFormData}
        />
      )}
      {steps[currentStep - 1] === "review" && (
        <ReviewForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={onContinue}
          currentStep={currentStep}
          totalSteps={steps.length}
          onBack={handleBack}
          onCancel={handleCancel}
          submitting={saving}
        />
      )}
    </div>
  );
}

export default ReviewFormWrapper; 